import { useQuery } from '@tanstack/react-query';
import type { AgentState } from '@khc/contracts';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ApiError } from '../data/client';
import { certificatesQueryOptions } from '../data/certs';
import { hubQueryOptions } from '../data/queries';
import { useHubChannelsContext } from '../app/HubChannelsProvider';
import { caddyConfigQueryOptions } from '../data/sites';
import { wireGuardConfigQueryOptions } from '../data/wireguard';
import { paths } from '../registry/routes';
import {
  Banner,
  Button,
  Card,
  Grid,
  KeyValue,
  Link,
  PageHeader,
  Row,
  Skeleton,
  Stack,
  Stat,
} from '../ui';
import { zh, fmt } from '../i18n/zh';
import { RecentOperations } from '../apps/audit/RecentOperations';
import { TodoChips, type TodoChip } from '../patterns/TodoChips';
import { formatAbsoluteTime } from '../i18n/format';
import { nodeModeQueryOptions } from '../data/mode';
import { ModeBadge } from '../apps/node-settings/ModeBadge';

export default function HubOverviewPage() {
  const { hubId } = useParams({ from: '/hubs/$hubId' });
  const navigate = useNavigate();
  const hubQuery = useQuery(hubQueryOptions(hubId));
  const { stateQuery, live, channels } = useHubChannelsContext();
  const wgQuery = useQuery(wireGuardConfigQueryOptions(hubId));
  const sitesQuery = useQuery(caddyConfigQueryOptions(hubId));
  const certsQuery = useQuery(certificatesQueryOptions());
  const modeQuery = useQuery(nodeModeQueryOptions(hubId));

  if (hubQuery.isLoading) {
    return (
      <Stack gap={4}>
        <PageHeader title={hubId} summary={zh.hub.summary} />
        <Skeleton height={96} />
        <Skeleton height={220} />
      </Stack>
    );
  }

  if (hubQuery.error) {
    const notFound = hubQuery.error instanceof ApiError && hubQuery.error.status === 404;
    return (
      <Stack gap={4}>
        <PageHeader title={zh.hub.notFound} />
        <Banner
          tone={notFound ? 'neutral' : 'danger'}
          title={fmt(zh.errors.loadTitle, { page: zh.hub.summary })}
          description={(hubQuery.error as Error).message}
          action={
            notFound ? undefined : (
              <Button label={zh.common.retry} size="sm" onClick={() => void hubQuery.refetch()} />
            )
          }
        />
      </Stack>
    );
  }

  const node = hubQuery.data;
  if (!node) {
    return (
      <Stack gap={4}>
        <PageHeader title={zh.hub.notFound} />
      </Stack>
    );
  }

  const state = stateQuery.data;
  const agentState = (live.snapshot?.state as unknown as AgentState | undefined) ?? state?.state;
  const host = agentState?.host;
  const ops = agentState?.ops;
  const peers = wgQuery.data?.config.peers ?? [];
  const sites = sitesQuery.data?.config.sites ?? [];
  const allCerts = certsQuery.data ?? [];

  const wgAdapter = state?.state?.adapters.find((adapter) => adapter.name === 'wireguard');
  const runtimePeers = (wgAdapter?.status as { peers?: { name: string; online?: boolean }[] } | undefined)?.peers;
  const onlinePeers = peers.filter(
    (peer) => runtimePeers?.find((item) => item.name === peer.name)?.online === true,
  ).length;

  // 统计当前节点签发或被当前节点站点绑定的证书
  const boundCertIds = new Set(
    sites.map((s) => s.tls.cert?.$cert).filter((id): id is string => Boolean(id)),
  );
  const nodeCerts = allCerts.filter(
    (cert) => cert.sourceNodeId === hubId || boundCertIds.has(cert.id),
  );

  const peerEnabled = modeQuery.data?.mode !== 'caddy';
  const cpuPct = ops?.cpu?.usagePercent ?? null;
  const memPct = ops?.mem?.percent ?? null;
  const diskPct = ops?.disks?.[0]?.percent ?? null;
  const agentVersion = agentState?.agent?.version;

  // 域名证书状态：只认 Agent 实际探测结果；拿不到就明说未知，不做「看起来成功」的兜底。
  const caddyAdapter = agentState?.adapters.find((adapter) => adapter.name === 'caddy-site');
  const caddyStatus = caddyAdapter?.status as
    | { error?: string; siteHealth?: { name?: string; domain?: string; state?: string; error?: string }[] }
    | undefined;
  const domainHealth = node.domain
    ? caddyStatus?.siteHealth?.find((site) => (site.domain ?? site.name) === node.domain)
    : undefined;
  const domainHasSite = node.domain ? sites.some((site) => site.domain === node.domain) : false;
  let certStatus: string;
  if (!node.domain) {
    certStatus = zh.hub.certNoDomain;
  } else if (!caddyStatus || caddyStatus.error) {
    certStatus = zh.hub.certUnknown;
  } else if (!domainHasSite && !domainHealth) {
    certStatus = zh.hub.certNoSite;
  } else if (!domainHealth) {
    certStatus = zh.hub.certUnknown;
  } else if (domainHealth.state === 'ok') {
    certStatus = zh.hub.certOk;
  } else if (domainHealth.state === 'warn') {
    certStatus = fmt(zh.hub.certWarn, { error: domainHealth.error ?? 'HTTPS 校验未通过' });
  } else if (domainHealth.state === 'down') {
    certStatus = fmt(zh.hub.certDown, { error: domainHealth.error ?? '站点不可达' });
  } else {
    certStatus = zh.hub.certUnknown;
  }

  // 「需要处理」聚合：服务异常 + 临期证书 + 长期离线 Peer（ui-redesign ③）
  const nowSec = Math.floor(Date.now() / 1000);
  const todos: TodoChip[] = [];
  for (const unit of ops?.services?.failedUnits ?? []) {
    todos.push({
      id: `unit:${unit}`,
      tone: 'danger',
      badge: zh.hub.todoFailedUnitBadge,
      name: unit,
      to: paths.hubSection(hubId, 'ops'),
    });
  }
  for (const cert of nodeCerts) {
    if (cert.notAfter === null) continue;
    const daysLeft = Math.ceil((cert.notAfter - nowSec) / 86400);
    if (daysLeft > 14 || daysLeft < 0) continue;
    todos.push({
      id: `cert:${cert.id}`,
      tone: 'warn',
      badge: zh.hub.todoCertBadge,
      name: cert.name,
      desc: fmt(zh.hub.todoCertDesc, { days: Math.max(0, daysLeft) }),
      to: `${paths.hubSection(hubId, 'sites')}?tab=certs`,
    });
  }
  if (peerEnabled) {
    for (const peer of peers) {
      if (!peer.enabled) continue;
      const online = runtimePeers?.find((item) => item.name === peer.name)?.online === true;
      if (online) continue;
      todos.push({
        id: `peer:${peer.name}`,
        tone: 'warn',
        badge: zh.hub.todoPeerBadge,
        name: peer.name,
        to: paths.hubSection(hubId, 'wireguard'),
      });
    }
  }

  return (
    <Stack gap={4}>
      <PageHeader
        title={node.name}
        summary={zh.hub.summary}
        sync={{
          state: node.status === 'online' ? 'synced' : 'queued',
          offlineHint: zh.hub.offlineBanner,
        }}
      >
        {modeQuery.data ? (
          <Row gap={2} wrap>
            <ModeBadge mode={modeQuery.data.mode} />
          </Row>
        ) : null}
      </PageHeader>

      {channels.presence === 'live-only' ? (
        <Banner tone="warn" title={zh.channels.liveOnlyTitle} description={zh.channels.liveOnlyDesc} />
      ) : null}
      {node.status === 'offline' && channels.presence !== 'live-only' ? (
        <Banner tone="neutral" title={zh.hub.offlineBanner} />
      ) : null}
      {channels.control.reachable === false && channels.presence !== 'live-only' ? (
        <Banner
          tone="danger"
          title={zh.hub.unreachableBanner}
          description={channels.control.error ?? state?.error ?? undefined}
          action={
            <Button label={zh.common.retry} size="sm" onClick={() => void stateQuery.refetch()} />
          }
        />
      ) : null}

      {/* 4 项核心指标：连接 · 站点 · Peer 在线 · 负载 */}
      <Grid cols={4}>
        <Stat
          label={zh.hub.statReachable}
          value={
            channels.control.reachable === true
              ? zh.status.online
              : channels.control.reachable === false
                ? zh.status.offline
                : '—'
          }
          unit={
            state?.route
              ? state.route === 'direct'
                ? zh.hubs.routeDirect
                : zh.hubs.routeTunnel
              : node.preferredRoute === 'direct'
                ? zh.hubs.routeDirect
                : zh.hubs.routeTunnel
          }
          hint={agentVersion ? `${node.endpoint} · Agent v${agentVersion}` : node.endpoint}
          icon="activity"
        />
        <Stat
          label={zh.hub.statSites}
          value={String(sites.length)}
          unit={sites.length > 0 ? `${sites.filter((s) => s.enabled).length} 启用` : undefined}
          hint={
            sites.length > 0
              ? '反代 / 静态 / 重定向'
              : '还没有站点，到站点页发布'
          }
          icon="globe"
        />
        <Stat
          label={zh.hub.statPeers}
          value={peerEnabled ? `${onlinePeers} / ${peers.length}` : '—'}
          hint={peerEnabled ? undefined : zh.hub.peerDisabledHint}
          icon="users"
        />
        <Stat
          label={zh.hub.statLoad}
          value={cpuPct !== null ? `${cpuPct.toFixed(1)}%` : '—'}
          unit={
            cpuPct !== null && memPct !== null
              ? `内存 ${memPct.toFixed(0)}%${diskPct !== null ? ` · 磁盘 ${diskPct.toFixed(0)}%` : ''}`
              : undefined
          }
          hint={host?.os ?? '按需拉取'}
          icon="terminal"
        />
      </Grid>

      {/* 需要处理：服务异常 + 临期证书 + 长期离线 Peer，chip 直达对应页 */}
      <TodoChips items={todos} onOpen={(to) => void navigate({ href: to })} />

      <Grid cols={2}>
        <Card
          title="运行信息"
          action={<Link to={paths.hubSection(hubId, 'ops')} label="主机运维 →" />}
        >
          <KeyValue
            items={[
              {
                key: zh.fields.endpoint,
                value: node.endpoint,
                mono: true,
                mode: 'middle',
                copyValue: node.endpoint,
              },
              {
                key: zh.hub.domainLabel,
                value: node.domain ?? zh.hub.certNoDomain,
                mono: Boolean(node.domain),
                ...(node.domain ? { copyValue: node.domain } : {}),
              },
              {
                key: zh.hub.certLabel,
                value: certStatus,
              },
              {
                key: '主机与系统',
                value: host ? `${host.hostname} · ${host.os}` : node.name,
              },
              {
                key: '服务版本',
                value: agentVersion ? `Agent v${agentVersion}` : '—',
                mono: true,
              },
              {
                key: '资源负载',
                value:
                  cpuPct !== null && memPct !== null
                    ? `CPU ${cpuPct.toFixed(1)}% · 内存 ${memPct.toFixed(0)}%${diskPct !== null ? ` · 磁盘 ${diskPct.toFixed(0)}%` : ''}`
                    : '按需拉取',
              },
              { key: zh.fields.lastSeen, value: formatAbsoluteTime(node.lastSeenAt) },
            ]}
          />
        </Card>

        <RecentOperations hubId={hubId} offline={node.status === 'offline'} />
      </Grid>
    </Stack>
  );
}
